import { BaseAPI } from "@utils/base-api";
import { HttpException } from "@nestjs/common";

import { EStatusCodes } from "@/constants/state";
import { DiscountApplication } from "@/primary-models";
import { TCreditReason } from "@/constants/discounts-and-credit";

import { TResponse as TRequestResponse } from "../../../api.types";

import { OrderType, SubscriptionType } from "./types/subscriptions";
import { IPossibleDeliveryDates } from "./types/delivery-dates";
import { ExtraProduct } from "./types/products";
import { TLegacyOrderCollection } from "./types/orders";

export class CustomerAPI extends BaseAPI {

  public async addDiscountCode(
    discountCode?: string,
  ): Promise<DiscountApplication> {
    if (discountCode === undefined) {
      throw new HttpException({ message: "No discount code" }, EStatusCodes.NOT_ACCEPTABLE);
    }

    try {
      const response = await this.axios.post(
        `/customer/discount/add?discountCode=${encodeURIComponent(discountCode)}`,

      );
      return response.data;
    } catch {
      throw new HttpException({ message: "Bad request" }, EStatusCodes.BAD_REQUEST);
    }

  }

  /** If a parameter is not set, then it is treated as if all the types apply.
   *  So if 2022-12-06 is blocked for fresh and 2022-12-07 is blocked for addon and
   *  subscriptionType is not set, then neither 2022-12-06 nor 2022-12-07 will be
   *  available in the returned data.
   *  @param orderType
   *  @param numberOfDays
   *  @param subscriptionType
   *  @param addonPlanType
   *  @param bankHoliday if set to true, then only bank holidays will be excluded. Else
   *  non-bank holiday delivery dates that have been disabled will be excluded as well.
   *  Should probably be false for most use cases. */
  public async getPossibleDeliveryDatesType(
    orderType: OrderType,
    numberOfDays?: number,
    subscriptionType?: SubscriptionType,
    addonPlanType?: ExtraProduct,
    bankHoliday?: boolean,
  ): Promise<IPossibleDeliveryDates> {
    const params = new URLSearchParams();
    params.set("orderType", orderType);
    if (numberOfDays) { params.set("numberOfDays", numberOfDays.toString()); }
    if (subscriptionType) { params.set("subscriptionType", subscriptionType); }
    if (addonPlanType) { params.set("addonPlanType", addonPlanType); }
    if (bankHoliday) { params.set("bankHoliday", "true"); }
    const result = await this.axios.get("/getPossibleDeliveryDatesType", { params });
    return result.data;
  }

  public async visitedPage(
    pageUrl: string,
    token: string,
  ): Promise<unknown> {
    const result = await this.axios.post(
      "/customer/visitedPage",
      { pageUrl },
      {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        withCredentials: true,
      },
    );
    return result.data;
  }

  public async getAllOrders(): Promise<TLegacyOrderCollection> {
    const result = await this.axios.get(
      "/customer/orders/all",
    );
    return result.data;
  }

  public async getLegacyLogs(customerId: string): TRequestResponse<LegacyCustomerEventLog[]> {
    try {
      const response = await this.axios.get(
        `/admin/customer/actions?customerId=${customerId}`,
      );
      return response.data;
    } catch (error) {
      return { error: error as Error };
    }
  }

  // This endpoint is used to refund a user only for ReCharge charges (not KatKin charges)
  // KatKin charges are refunded using the ChargesAdminAPI.createRefund endpoint
  public async refundUser(
    data: IRefundUserRequest,
  ): TRequestResponse<void> {
    try {
      const response = await this.axios.post(
        `/admin/customer/refund?chargeId=${data.rechargeChargeId}&amount=${data.refundAmount}&cancelOrder=${data.cancelOrder}&cxUser=${data.cxUser}`,
        {
          refundReason: data.refundReason,
          ticketUrl: data.ticketUrl,
        },
      );
      return response.data;
    } catch (error) {
      return { error: error as Error };
    }
  }

  public async resetPassword(email: string): TRequestResponse<void> {
    try {
      const response = await this.axios.post(
        "/customer/recover",
        { email },
      );
      return response.data;
    } catch (error) {
      return { error: error as Error };
    }
  }

  // @param customerId - should be the customer's oldId
  public async getCustomerInteractions(customerId: string): TRequestResponse<CustomerInteractionCollection> {
    try {
      const response = await this.axios.get(
        `/admin/customer/interactions?customerId=${customerId}`,
      );
      return response.data;
    } catch (error) {
      return { error: error as Error };
    }
  }

}

export interface IRefundUserRequest {
  rechargeChargeId?: number,
  katkinChargeId?: number,
  refundAmount: number,
  cancelOrder: boolean,
  refundReason: TCreditReason,
  ticketUrl: string,
  cxUser: string;
}

export type LegacyCustomerEventLog = {
  id: number;
  customerId: string;
  name: string;
  metadata: string | null | undefined;
  createdAt: string;
};


export interface CustomerInteractionResource {
  customerId: string;
  createdAt: string; // ISO datetime
  data: InteractionData;
  triggeredBy: string | null;
}

export type CustomerInteractionCollection = CustomerInteractionResource[];

export interface InteractionData {
  message: string;
  interaction: InteractionName;
  name: InteractionName;
}

export type InteractionName =
  | "QUESTION_CAT_BOT"
  | "DELAY_TWO_WEEKS"
  | "DELAY_ONE_WEEK"
  | "QUESTION_BELIEF_IN_FRESH"
  | "ANSWER_BELIEF_IN_FRESH_FRESH_IS_GOOD"
  | "UOR_NO_DELAY"
  | "MISUNDERSTANDING_UOR_NO_DELAY"
  | "QUESTION_CHICKEN_NIBBLES_UPSELL"
  | "MISUNDERSTANDING_CHICKEN_NIBBLES_UPSELL"
  | "ANSWER_CHICKEN_NIBBLES_UPSELL"
  | "TRANSITIONING_INFO"
  | "QUESTION_HALF_MEAL_PLAN"
  | "SWITCH_TO_HALF_MEAL_PLAN"
  | "QUESTION_SCOOP_HEALTH_UPSELL"
  | "ANSWER_SCOOP_HEALTH_UPSELL_4_BAGS"
  | "MISUNDERSTANDING_CAT_BOT"
  | "QUESTION_NEW_PRODUCT_TRANSITION_SMS_2_TRAYS"
  | "ANSWER_YES_TRANSITIONED_WELL_V1"
  | "ANSWER_YES_TRANSITIONED_WELL_V2"
  | "ANSWER_TO_UNDER_8_RATING"
  | "ANSWER_TO_OVER_8_RATING"
  | "ONE_SHOT_NEW_PRODUCT_TRANSITIONING_SMS1_NO_TRAYS"
  | "TOO_LATE_REPLY"
  | "UNKNOWN"
  | "QUESTION_GENERIC"
  | "QUESTION_CAT_BOT_LITTER";